import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, categoryBySlug, imgSrc } from "../lib/api";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    setArticle(null);
    api
      .get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return api.get("/articles", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => r && setRelated(r.data.filter((x) => x.slug !== slug).slice(0, 3)))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl p-10 text-center">
        <h1 className="font-display-black text-5xl">Article introuvable</h1>
        <Link to="/" className="brutal-btn brutal-btn-yellow mt-6">
          ← Retour à l&apos;accueil
        </Link>
      </div>
    );
  }
  if (!article) return <div className="p-10 text-sm">Chargement…</div>;

  const cat = categoryBySlug(article.category);

  return (
    <div data-testid="article-page">
      <article>
        <header
          className="border-b-2 border-black"
          style={{ background: cat?.color, color: cat?.textColor }}
        >
          <div className="mx-auto max-w-[1100px] px-5 lg:px-10 py-12">
            <Link
              to={`/category/${article.category}`}
              className="text-xs uppercase font-bold tracking-widest opacity-80 hover:opacity-100 flex items-center gap-2"
            >
              <ArrowLeft size={14} /> {cat?.label}
            </Link>
            <h1 className="font-display-black text-4xl sm:text-6xl lg:text-7xl mt-5 leading-none">
              {article.title}
            </h1>
            <p className="mt-4 text-base sm:text-lg max-w-3xl">{article.excerpt}</p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs uppercase font-bold tracking-wide">
              <span className="pill-tag bg-white">par {article.author}</span>
              <span className="pill-tag bg-white">{article.read_time} min</span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-[1100px] px-5 lg:px-10">
          <div className="border-2 border-black rounded-2xl overflow-hidden -mt-1 shadow-[8px_8px_0_#050505]">
            <img src={imgSrc(article.cover_image)} alt={article.title} className="w-full aspect-[16/9] object-cover" />
          </div>
        </div>

        <div className="mx-auto max-w-[760px] px-5 lg:px-0 py-16 prose-content">
          {article.content.split("\n\n").map((para, i) => {
            const trimmed = para.trim();

            // Blockquote: > text
            if (trimmed.startsWith("> ")) {
              return (
                <blockquote
                  key={i}
                  className="border-l-4 border-[#FA6157] pl-5 my-8 font-display text-3xl leading-tight"
                >
                  {trimmed.replace(/^>\s+/, "")}
                </blockquote>
              );
            }

            // Heading H2: ## text (grand sous-titre)
            if (trimmed.startsWith("## ")) {
              return (
                <h2
                  key={i}
                  className="font-display-black text-4xl sm:text-5xl mt-14 mb-4 leading-none text-[#050505]"
                >
                  {trimmed.replace(/^##\s+/, "")}
                </h2>
              );
            }

            // Heading H3: ### text (petit sous-titre)
            if (trimmed.startsWith("### ")) {
              return (
                <h3
                  key={i}
                  className="font-display text-2xl sm:text-3xl mt-10 mb-3 text-[#0A8074]"
                >
                  {trimmed.replace(/^###\s+/, "")}
                </h3>
              );
            }

            // Bullet list: lines starting with - or *
            if (/^([-*])\s/.test(trimmed) && trimmed.split("\n").every((l) => /^([-*])\s/.test(l.trim()) || l.trim() === "")) {
              const items = trimmed
                .split("\n")
                .filter((l) => l.trim())
                .map((l) => l.trim().replace(/^([-*])\s+/, ""));
              return (
                <ul key={i} className="list-disc pl-6 mb-6 space-y-2 text-lg">
                  {items.map((it, j) => (
                    <li
                      key={j}
                      dangerouslySetInnerHTML={{
                        __html: it
                          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#0A8074]">$1</strong>')
                          .replace(/\*(.+?)\*/g, '<em>$1</em>'),
                      }}
                    />
                  ))}
                </ul>
              );
            }

            // Default paragraph with inline **bold** and *italic*
            const html = para
              .replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#0A8074]">$1</strong>')
              .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
              .replace(/\n/g, "<br/>");
            return (
              <p
                key={i}
                className="text-lg leading-relaxed mb-6"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          })}
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-[#FFD01E] border-y-2 border-black py-16">
          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
            <h2 className="font-display-black text-4xl sm:text-5xl mb-8">
              On en parlait aussi.
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/article/${a.slug}`}
                  data-testid={`related-${a.slug}`}
                  className="brutal-card overflow-hidden block group bg-[#FFFDF7]"
                >
                  <div className="aspect-[5/4] overflow-hidden border-b-2 border-black">
                    <img
                      src={imgSrc(a.cover_image)}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-2xl">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
